import React, { FC, useEffect } from 'react'
import {
  useDisclosure,
  Modal,
  ModalContent,
  ModalBody,
  Icon,
  Textarea,
  Button,
  Box,
  Image,
  Heading,
  Text,
  ModalCloseButton,
  Link,
} from '@chakra-ui/core'
import { useRecoilValue, useRecoilState } from 'recoil'
import { configState, cartState, orderConfigState } from '../../recoil/atoms'
import { cartStatsState } from '../../recoil/selectors'
import { CartItem } from '../../components/CartItem'
import { LinkButton } from '../../lib/types'
import { Button as ButtonComponent } from '@engyalo/storefront-components'
import { useMutation, useQuery } from '@apollo/client'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { createOrderMutation, customCart } from '../../queries'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import {
  createOrderVariables,
  createOrder_createOrder as ICreateOrder,
} from '@engyalo/storefront-types/lib/graphql/order'
import {
  customCartVariables,
  customCart as ICustomCart,
} from '@engyalo/storefront-types/lib/graphql/cart'
import { OrderStatus } from '@engyalo/storefront-types/lib/graphql/global-types'

export const Cart: FC = () => {
  const { t } = useTranslation()
  const { customer, order } = useParams()
  const config = useRecoilValue(configState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const [cart, setCart] = useRecoilState(cartState)
  const { totalNum, totalPrice } = useRecoilValue(cartStatsState)

  const [message, setMessage] = React.useState('')
  const [isCartVerified, setCartIsVerified] = React.useState(false)

  const { isOpen, onOpen, onClose } = useDisclosure()

  useQuery<ICustomCart, customCartVariables>(customCart, {
    variables: {
      storefrontID: config.storefrontId,
      orderConfigID: orderConfig.id || '',
      /**
       * This is ugly but needed by now. Product query gets an
       * autogenerated `__typename` property which is useful for caching
       * but `customCart` query doesn't expect it, thus we need to remove
       * it from the cart items list, however TS complains because it
       * doesn't find a `__typename` property in the IProduct interface :(
       * TODO: Remove @ts-ignore by ensuring the right types in all places
       */
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      cart: Object.values(cart).map(({ __typename, ...product }) => product),
    },
    skip: !config.enabledCustomCart || isCartVerified,
    onCompleted(data: ICustomCart) {
      const verifiedCart = data.customCart.reduce(
        (prev, cartItem) => ({
          ...prev,
          [cartItem?.id || '']: cartItem,
        }),
        {}
      )
      setCartIsVerified(true)
      setCart(verifiedCart)
    },
  })

  const deleteCart = (): void => {
    window.analytics.track('Reset Cart Complete', {
      'Cart Size': totalNum,
      'Cart Value': Number(totalPrice),
      'Cart Product IDs': Object.values(cart).map((product) => product.id),
      'Cart Product SKUs': Object.values(cart).map((product) => product.sku),
      'Cart Product Names': Object.values(cart).map((product) => product.name),
      'Cart Product Categories': Object.values(cart).map(
        (product) => product.category
      ),
      'Custom Note Text': message,
      'User ID': orderConfig.userID,
      'Customer Name': orderConfig.channelID,
    })
    setCart({})
    onClose()
  }
  const onTextChange = (e: React.FormEvent<HTMLInputElement>): void => {
    setMessage(e.currentTarget.value)
  }

  const [createOrder] = useMutation<ICreateOrder, createOrderVariables>(
    createOrderMutation,
    {
      variables: {
        order: {
          storefrontID: config.storefrontId,
          userID: orderConfig.userID || '',
          status: OrderStatus.ENABLE,
          provider: orderConfig.channelName,
          total: parseFloat(totalPrice),
          totalQty: totalNum,
          cart: Object.keys(cart).map((key) => cart[key]),
          note: message,
        },
        configId: order,
      },
      onCompleted: () => {
        if (orderConfig.channelName === 'whatsapp') {
          window.location.href = `http://wa.me/${orderConfig.channelID}`
        }
      },
    }
  )

  useEffect(() => {
    window.analytics.track('Cart Viewed', {
      'Cart Size': totalNum,
      'Cart Value': Number(totalPrice),
      'Cart Product IDs': Object.values(cart).map((product) => product.id),
      'Cart Product SKUs': Object.values(cart).map((product) => product.sku),
      'Cart Product Names': Object.values(cart).map((product) => product.name),
      'Cart Product Categories': Object.values(cart).map(
        (product) => product.category
      ),
      'Custom Products?': Object.values(cart).some(
        (product) =>
          Array.isArray(product.customizations) &&
          product.customizations.length > 1
      ),
      'Custom Note Text': message,
      'User ID': orderConfig.userID,
      'Customer Name': orderConfig.channelID,
    })
  }, [])

  return (
    <Box h="100%" w="100%" d="flex" flexDir="column">
      {isOpen && (
        <Box
          position="absolute"
          opacity={0.5}
          backgroundColor="black"
          width="100%"
          height="100%"
          zIndex={50}
        />
      )}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalContent>
          <ModalCloseButton
            background="transparent"
            borderStyle="none"
            borderColor="unset"
          />
          <ModalBody>
            <Heading
              as="h3"
              fontSize="md"
              textAlign="center"
              paddingTop="40px"
              paddingBottom="16px"
            >
              {t('cart-removeAllQuestion')}
              <span role="img" aria-label="scream">
                😱
              </span>
            </Heading>
            <Box paddingBottom="20px">
              <ButtonComponent onClick={deleteCart} danger>
                {t('cart-removeAll')}
              </ButtonComponent>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Box h="40px" d="flex">
        <LinkButton
          to={`/${customer}/${order}`}
          h="100%"
          w="32px"
          background="transparent"
          borderColor="transparent"
          size="lg"
          aria-label="cart"
          icon="chevron-left"
        />
        <Image
          alt="logo"
          h="36px"
          alignSelf="center"
          margin="auto"
          src={config.logo}
        />
      </Box>
      <Box
        padding="12px 12px 8px 16px"
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <Heading as="h2" fontSize="lg" fontWeight="normal">
          {t('cart-title')}
        </Heading>
        {totalNum > 0 && (
          <Box display="flex" justifyContent="space-between">
            <Text fontSize="sm" color="gray.100">
              {t('cart-resume', { count: totalNum })}
            </Text>
            <Button
              color="primary.500"
              variant="link"
              border="transparent"
              fontSize="sm"
              textDecor="underline"
              backgroundColor="transparent"
              fontWeight="normal"
              onClick={onOpen}
            >
              {t('cart-cleanCart')}
            </Button>
          </Box>
        )}
      </Box>
      {/* Body */}
      <Box display="flex" flex="1" flexDir="column">
        {totalNum > 0 ? (
          Object.keys(cart).map((key) => (
            <CartItem key={key} product={cart[key]} />
          ))
        ) : (
          <Box
            display="flex"
            flexDir="column"
            justifyContent="center"
            alignItems="center"
            h="100%"
          >
            <Icon name="settings" size="48px" paddingBottom="8px" />
            <Text fontSize="md" textAlign="center" whiteSpace="pre-line">
              {t('cart-emptyMessage')}
            </Text>
            <Link
              color="primary.500"
              href={`/${customer}/${order}`}
              aria-label="backToCart"
              border="transparent"
              fontSize="sm"
              textDecor="underline"
              backgroundColor="transparent"
              fontWeight="normal"
              paddingTop="32px"
            >
              {t('cart-backCatalogue')}
            </Link>
          </Box>
        )}
      </Box>
      {/* Body */}
      {totalNum > 0 && config.enabledOrderNote && (
        <Box paddingX="8px">
          <Text display="inline-flex" fontSize="sm">
            {t('cart-observations')}
          </Text>
          <Text
            display="inline-flex"
            paddingLeft="4px"
            fontSize="sm"
            color="gray.100"
          >
            {t('cart-observationsAlter')}
          </Text>
          <Textarea
            marginTop="8px"
            value={message}
            onChange={onTextChange}
            placeholder={t('cart-observationsPlaceholder')}
            backgroundColor="gray.300"
            borderColor="gray.200"
            borderRadius="4px"
            resize="none"
            size="sm"
          />
        </Box>
      )}
      {/* Footer */}
      <Box
        zIndex={1}
        paddingY="12px"
        paddingX="8px"
        boxShadow="md"
        backgroundColor="white"
      >
        <Box
          display="flex"
          width="100%"
          paddingY="12px"
          paddingX="16px"
          backgroundColor="primary.500"
          borderColor="transparent"
          fontWeight="500"
          color="white"
          fontFamily="body"
          fontSize="11px"
          borderRadius="4px"
          justifyContent="space-between"
          as="button"
          onClick={(): void => {
            window.analytics.track('Order Sent', {
              'Cart Size': totalNum,
              'Cart Value': Number(totalPrice),
              'Cart Product IDs': Object.values(cart).map(
                (product) => product.id
              ),
              'Cart Product Names': Object.values(cart).map(
                (product) => product.name
              ),
              'Cart Product SKUs': Object.values(cart).map(
                (product) => product.sku
              ),
              'Cart Product Categories': Object.values(cart).map(
                (product) => product.category
              ),
              'Custom Products?': Object.values(cart).some(
                (product) =>
                  Array.isArray(product.customizations) &&
                  product.customizations.length > 1
              ),
              'Custom Note Text': message,
              'User ID': orderConfig.userID,
              'Customer Name': orderConfig.channelID,
            })
            createOrder()
          }}
        >
          <Text fontSize="lg" alignSelf="center">
            {t('cart-pay')}
          </Text>
          <Text
            fontSize="lg"
            alignSelf="center"
          >{`${config.currencyPrefix} ${totalPrice}`}</Text>
        </Box>
      </Box>
      {/* Footer */}
    </Box>
  )
}
